// features/cx/payments/api.ts

import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ConsumerSearchResult,
  ConsumerMetaData,
  PaymentManagementSummary,
  CreditNoteSummary,
  LatestConsumerBill,
  PaymentServices,
  CreatePaymentPayload,
  CreateCreditNotePayload,
  PaymentDetail,
  ConsumerSearchFilters,
  PaymentFilters,
  CreditNoteFilters,
  CreditNote,
  PaymentPayType,
  Installment,
  PaymentChannel,
  PaymentReceivedStatus,
} from "./types";
import { PaginatedResponse } from "@features/onboarding/utility-setup/types";
import { DropdownItem } from "@shared/api/types";
import { PaymentMethod } from "../shared/types/consumerRequest";

// API Functions
export const paymentsApi = {
  // Consumer Search (shared functionality)
  searchConsumer: async (
    filters: ConsumerSearchFilters,
  ): Promise<{ result: ConsumerSearchResult[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-search/info",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("search_data", filters.searchData);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data,
    );
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }): Promise<{ result: ConsumerMetaData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerMetaData }>(response.data);
  },

  // Payment Management Summary
  getPaymentManagementSummary: async (
    remoteUtilityId: number,
  ): Promise<{ result: PaymentManagementSummary }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-management/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: PaymentManagementSummary }>(
      response.data,
    );
  },

  // Credit Note Summary
  getCreditNoteSummary: async (
    remoteUtilityId: number,
  ): Promise<{ result: CreditNoteSummary }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: CreditNoteSummary }>(response.data);
  },

  // Latest Consumer Bill
  getLatestConsumerBill: async (params: {
    remoteUtilityId: number;
    consumerId: number;
    consumerNo: string;
  }): Promise<{ result: LatestConsumerBill[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "latest-consumer-bill",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_id", params.consumerId.toString());
        query.push("consumer_no", params.consumerNo);
        return query;
      },
    );

    const response = await cxApiClient.get<{ result: LatestConsumerBill[] }>(
      url,
    );
    return transformJsonToObject<{ result: LatestConsumerBill[] }>(
      response.data,
    );
  },

  // Payment Services
  getPaymentServices: async (params: {
    remoteUtilityId: number;
    consumerId: number;
  }): Promise<{ result: PaymentServices }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-services",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_id", params.consumerId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get<{ result: PaymentServices }>(url);
    return transformJsonToObject<{ result: PaymentServices }>(response.data);
  },

  // Create Payment
  createPayment: async (payload: CreatePaymentPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "payment");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  createPlanPayment: async (payload: CreatePaymentPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "payment");

    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  // Create Credit Note
  createCreditNote: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "credit-note");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },
  //https://api-cx-mercury.bynry.com/api/cx/payment-service-order/?remote_utility_id=226&consumer_id=11613

  getServiceOrder: async (params: {
    remote_utility_id: string;
    consumer_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-service-order",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("consumer_id", params.consumer_id);
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Get Credit Notes List
  getCreditNotes: async (
    filters: CreditNoteFilters,
  ): Promise<PaginatedResponse<CreditNote>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType && filters.paymentPayType.length > 0) {
          filters.paymentPayType.forEach((type) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<CreditNote>>(response.data);
  },

  exportCreditNotesList: async (filters: CreditNoteFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType && filters.paymentPayType.length > 0) {
          filters.paymentPayType.forEach((type) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  // Get Payment Management Detail
  getCreditNoteDetail: async (params: {
    creditNoteId: number;
    remoteUtilityId: number;
  }): Promise<{ result: CreditNote }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "credit-note",
      ["detail", params.creditNoteId.toString()],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get<{ result: CreditNote }>(url);
    return transformJsonToObject<{ result: CreditNote }>(response.data);
  },

  // Get Payment Management Detail
  getPaymentDetail: async (params: {
    paymentId: number;
    remoteUtilityId: number;
  }): Promise<{ result: PaymentDetail }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "payment-management",
      ["detail", params.paymentId.toString()],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Choice APIs
  getCreditNoteReasons: async (): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["credit_note_reason"],
      (query) => {
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getPaymentReceivedStatusChoices: async (
    remoteUtilityId: number,
  ): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["payment_received_status"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get<{ result: DropdownItem[] }>(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getPaymentChannelChoices: async (
    remoteUtilityId: number,
  ): Promise<{ result: PaymentChannel[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_channel"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get<{ result: PaymentChannel[] }>(url);
    return transformJsonToObject<{ result: PaymentChannel[] }>(response.data);
  },

  getPaymentPayTypeChoices: async (
    remoteUtilityId: number,
  ): Promise<{ result: PaymentPayType[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["payment_pay_type"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get<{ result: PaymentPayType[] }>(url);
    return transformJsonToObject<{ result: PaymentPayType[] }>(response.data);
  },

  getPaymentStatusChoices: async (
    remoteUtilityId: number,
  ): Promise<{ result: PaymentReceivedStatus[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["payment_received_status"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get<{ result: PaymentReceivedStatus[] }>(
      url,
    );
    return transformJsonToObject<{ result: PaymentReceivedStatus[] }>(
      response.data,
    );
  },

  // Additional payment-related endpoints can be added here
  getPayments: async (
    filters: PaymentFilters,
  ): Promise<PaginatedResponse<PaymentDetail>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType && filters.paymentPayType.length > 0) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<PaymentDetail>>(
      response.data,
    );
  },

  exportPaymentsList: async (filters: PaymentFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType && filters.paymentPayType.length > 0) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Update Payment (if needed)
  updatePayment: async (
    paymentId: number,
    payload: Partial<CreatePaymentPayload>,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payment", [
      paymentId.toString(),
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  // Delete Payment (if needed)
  deletePayment: async (paymentId: number): Promise<void> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payment", [
      paymentId.toString(),
    ]);
    await cxApiClient.delete(url);
  },

  getInstallments: async (params: {
    remoteUtilityId: number;
    consumerId: number;
    consumerNo: string;
  }): Promise<{ result: Installment[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-installment",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_id", params.consumerId.toString());
        query.push("consumer_no", params.consumerNo);
        return query;
      },
    );
    const response = await cxApiClient.get<{ result: Installment[] }>(url);
    return transformJsonToObject<{ result: Installment[] }>(response.data);
  },

  getPaymentMethod: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: PaymentMethod[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_mode"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<{ result: PaymentMethod[] }>(response.data);
  },
};
